import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import api from '../shared/services/api';

export interface CardVisibilityMap {
  document_processing: boolean;
  tampering_detection: boolean;
  email_processor: boolean;
  tariff_classifier: boolean;
  export_control: boolean;
  cross_document: boolean;
  magic_marker: boolean;
  compliance_engine: boolean;
  customs_audit: boolean;
  document_classifier: boolean;
  pdf_security_scanner: boolean;
  nlp_rules: boolean;
  ai_learning: boolean;
  business_intelligence: boolean;
  conversational_ai: boolean;
  workflow_automation: boolean;
  blockchain: boolean;
  multi_language: boolean;
  master_data: boolean;
  predictive_analytics: boolean;
  smart_contract: boolean;
  intelligent_routing: boolean;
  supply_chain: boolean;
  document_quality: boolean;
  version_control: boolean;
  admin_panel: boolean;
  [key: string]: boolean;
}

const defaultCards: CardVisibilityMap = {
  document_processing: true,
  tampering_detection: true,
  email_processor: true,
  tariff_classifier: true,
  export_control: false,
  cross_document: true,
  magic_marker: true,
  compliance_engine: true,
  customs_audit: true,
  document_classifier: true,
  pdf_security_scanner: true,
  nlp_rules: true,
  ai_learning: false,
  business_intelligence: false,
  conversational_ai: false,
  workflow_automation: false,
  blockchain: false,
  multi_language: false,
  master_data: true,
  predictive_analytics: false,
  smart_contract: false,
  intelligent_routing: false,
  supply_chain: false,
  document_quality: false,
  version_control: false,
  admin_panel: false,
};

interface CardVisibilityState {
  cards: CardVisibilityMap;
  isLoading: boolean;
  fetchCardVisibility: () => Promise<void>;
  saveCardVisibility: (cards: CardVisibilityMap) => Promise<void>;
  isCardVisible: (key: string) => boolean;
}

export const useCardVisibilityStore = create<CardVisibilityState>()(
  persist(
    (set, get) => ({
      cards: defaultCards,
      isLoading: false,

      fetchCardVisibility: async () => {
        try {
          set({ isLoading: true });
          const response = await api.get('/api/v1/admin/settings/card-visibility');
          const data = response.data;

          if (data && data.cards) {
            // Ensure all default cards are included, with server data taking priority
            const mergedCards = { ...defaultCards, ...data.cards };
            set({ cards: mergedCards });
          }
        } catch (error) {
          console.debug('Could not fetch card visibility settings, using local fallback');
          // Ensure we have all default cards even on error
          set({ cards: { ...defaultCards } });
        } finally {
          set({ isLoading: false });
        }
      },

      saveCardVisibility: async (cards: CardVisibilityMap) => {
        try {
          await api.put('/api/v1/admin/settings/card-visibility', { cards });
          set({ cards });
        } catch (error) {
          console.error('Could not save card visibility settings:', error);
          throw error;
        }
      },

      isCardVisible: (key: string) => {
        return get().cards[key] ?? false;
      },
    }),
    {
      name: 'card-visibility-storage',
      // Ensure persisted data is merged with defaults to include new cards
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.cards = { ...defaultCards, ...state.cards };
        }
      },
    }
  )
);

export const loadCardVisibilityOnLogin = () => {
  useCardVisibilityStore.getState().fetchCardVisibility();
};
